import { useEffect, useRef, useState } from "react";
import { onValue, ref } from "firebase/database";
import { database } from "../../firebase";

/** Rolling window. Beyond this the chart stops being readable anyway. */
export const MAX_POINTS = 10000;

/**
 * Extract the last `limit` CSV tokens from a growing comma-delimited string
 * without splitting the whole thing — these nodes reach hundreds of kilobytes
 * during a live capture.
 */
export const parseCsvTail = (raw, limit = MAX_POINTS, filter) => {
  if (typeof raw !== "string" || raw.length === 0) return [];

  let commas = 0;
  let index = raw.length - 1;
  while (index >= 0 && commas < limit) {
    if (raw.charCodeAt(index) === 44 /* , */) commas += 1;
    index -= 1;
  }

  const values = raw
    .slice(Math.max(0, index + 1))
    .split(",")
    .filter((token) => token !== "")
    .map(Number)
    .filter(Number.isFinite);

  const kept = filter ? values.filter(filter) : values;
  return kept.slice(-limit);
};

const extremes = (values) => {
  let min = Infinity;
  let max = -Infinity;
  for (const value of values) {
    if (value === null || value === undefined) continue;
    if (value < min) min = value;
    if (value > max) max = value;
  }
  return min === Infinity ? { min: null, max: null } : { min, max };
};

/**
 * Subscribe to a single live channel plus its status/progress siblings.
 *
 * Every real-time panel used to hand-roll this same subscription with slightly
 * different reset semantics; they now share one implementation, so "progress
 * went back to 0" always means the same thing.
 *
 * @param {string} watchId
 * @param {object} paths  { data, status, progress } absolute RTDB paths
 * @param {function} filter  optional sample validity predicate
 */
export const useRealtimeSeries = (watchId, paths, filter) => {
  const [values, setValues] = useState([]);
  const [status, setStatus] = useState("");
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // `paths` may be null while a caller is still resolving which node names
    // this firmware uses (see ElectrodermalActivityRealTime).
    if (!watchId || !paths) return undefined;

    const unsubscribeData = onValue(ref(database, paths.data), (snapshot) => {
      setValues(parseCsvTail(snapshot.val(), MAX_POINTS, filter));
    });

    const unsubscribeStatus = onValue(ref(database, paths.status), (snapshot) =>
      setStatus(snapshot.val() || "")
    );

    const unsubscribeProgress = onValue(ref(database, paths.progress), (snapshot) => {
      const next = snapshot.val() ?? 0;
      setProgress(next);
      // A reset to zero means a new capture started — drop the previous trace
      // rather than letting the two run together.
      if (next === 0) setValues([]);
    });

    return () => {
      unsubscribeData();
      unsubscribeStatus();
      unsubscribeProgress();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchId, paths?.data, paths?.status, paths?.progress]);

  const { min, max } = extremes(values);
  const samples = values.map((_, index) => index + 1);

  return { values, samples, status, progress, min, max };
};

/**
 * Same contract for multi-channel captures (PPG's three emitters, the
 * accelerometer's three axes). Channels are aligned to the longest one and
 * padded with nulls so a lagging channel does not shift the others.
 *
 * @param {object} paths  { channels: {key: path}, status, progress }
 */
export const useRealtimeChannels = (watchId, paths, filter) => {
  const buffers = useRef({});
  const [state, setState] = useState({ samples: [], channels: {} });
  const [status, setStatus] = useState("");
  const [progress, setProgress] = useState(0);

  const keys = Object.keys(paths.channels);
  const signature = keys.map((key) => paths.channels[key]).join("|");

  useEffect(() => {
    if (!watchId) return undefined;

    const recompute = () => {
      const lengths = keys.map((key) => buffers.current[key]?.length ?? 0);
      const longest = Math.max(0, ...lengths);

      if (longest === 0) {
        setState({ samples: [], channels: {} });
        return;
      }

      const start = Math.max(0, longest - MAX_POINTS);
      const length = longest - start;

      const samples = new Array(length);
      for (let i = 0; i < length; i += 1) samples[i] = start + i + 1;

      const channels = {};
      for (const key of keys) {
        const source = buffers.current[key] ?? [];
        const out = new Array(length);
        for (let i = 0; i < length; i += 1) {
          const index = start + i;
          out[i] = index < source.length ? source[index] : null;
        }
        channels[key] = out;
      }

      setState({ samples, channels });
    };

    const unsubscribers = keys.map((key) =>
      onValue(ref(database, paths.channels[key]), (snapshot) => {
        buffers.current[key] = parseCsvTail(snapshot.val(), MAX_POINTS, filter);
        recompute();
      })
    );

    unsubscribers.push(
      onValue(ref(database, paths.status), (snapshot) =>
        setStatus(snapshot.val() || "")
      )
    );

    unsubscribers.push(
      onValue(ref(database, paths.progress), (snapshot) => {
        const next = snapshot.val() ?? 0;
        setProgress(next);
        if (next === 0) {
          buffers.current = {};
          setState({ samples: [], channels: {} });
        }
      })
    );

    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchId, signature, paths.status, paths.progress]);

  const all = keys.flatMap((key) => state.channels[key] ?? []);
  const { min, max } = extremes(all);

  return { ...state, status, progress, min, max };
};

/** Mean of the last `window` finite samples, as a fixed-precision string. */
export const trailingAverage = (values = [], window = 49, digits = 2) => {
  const slice = values.slice(-window).filter((value) => Number.isFinite(value));
  if (!slice.length) return "—";
  return (slice.reduce((sum, value) => sum + value, 0) / slice.length).toFixed(digits);
};
